import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Users, Phone, Star, Award, Shield, CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { hotelInfo, rooms, facilities, testimonials } from '../data/mock';
import { DatePicker } from '../components/DatePicker';
import SEO from '../components/SEO';
import { getHotelSchema, getLocalBusinessSchema } from '../utils/schemaMarkup';
import { toast } from 'sonner';

const Home = () => {
  const [bookingData, setBookingData] = useState({
    name: '',
    phone: '',
    checkIn: null,
    checkOut: null,
    guests: '2',
  });

  const hotelSchema = getHotelSchema();
  const localBusinessSchema = getLocalBusinessSchema();

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!bookingData.checkIn || !bookingData.checkOut) {
      toast.error('Please select check-in and check-out dates');
      return;
    }
    
    // Validate dates
    if (bookingData.checkOut <= bookingData.checkIn) {
      toast.error('Check-out date must be after check-in date');
      return;
    }
    
    try {
      // Send to backend API
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      
      const response = await fetch(`${BACKEND_URL}/api/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: bookingData.name,
          phone: bookingData.phone,
          check_in: bookingData.checkIn.toISOString().split('T')[0],
          check_out: bookingData.checkOut.toISOString().split('T')[0],
          guests: parseInt(bookingData.guests) || 2,
        }),
      });
      
      // Always send WhatsApp message with all details
      // Prepare WhatsApp message with all user details
      const whatsappMessage = `*New Booking Inquiry - Check Availability*
    
👤 *Name:* ${bookingData.name || 'Not provided'}
📞 *Phone:* ${bookingData.phone || 'Not provided'}
📅 *Check-in Date:* ${bookingData.checkIn ? bookingData.checkIn.toLocaleDateString('en-IN', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }) : 'Not selected'}
📅 *Check-out Date:* ${bookingData.checkOut ? bookingData.checkOut.toLocaleDateString('en-IN', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }) : 'Not selected'}
👥 *Number of Guests:* ${bookingData.guests || '2'}

Please confirm availability and pricing.`;
      
      // Send to WhatsApp number 8941000583
      const whatsappNumber = '918941000583';
      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;
      
      // Open WhatsApp
      window.open(whatsappUrl, '_blank');
      
      if (response.ok) {
        toast.success('Booking inquiry submitted! Opening WhatsApp...');
        
        // Reset form
        setBookingData({ name: '', phone: '', checkIn: null, checkOut: null, guests: '2' });
      } else {
        toast.success('Thank you for your inquiry. We will get back to you soon.');
      }
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('An error occurred. Please try again or contact us directly.');
    }
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Hotel Suvidha Deluxe | Best Hotel Near Har Ki Pauri Haridwar"
        description="Book Hotel Suvidha Deluxe, best budget hotel near Har Ki Pauri Haridwar. Clean rooms, free WiFi, parking, and family-friendly stay."
        keywords="hotel in haridwar, best hotel haridwar, hotel near har ki pauri, haridwar hotel booking, budget hotel haridwar, luxury hotel haridwar, hotel near haridwar railway station, hotels in haridwar near ganga, hotel suvidha deluxe, haridwar accommodation, uttarakhand hotels, pilgrimage hotels haridwar"
        canonicalUrl="https://hotelsuvidhadeluxe.com/"
        schemaMarkup={[hotelSchema, localBusinessSchema]}
      />
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <picture>
            <source srcSet="/images/reception.webp" type="image/webp" />
            <img
              src="/images/reception.jpg"
              alt="Hotel Suvidha Deluxe Reception"
              fetchPriority="high"
              width={1200}
              height={800}
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900/90 via-navy-900/70 to-transparent"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 font-serif leading-tight animate-fade-in">
              Experience Luxury in <span className="text-gold-400">Haridwar</span>
            </h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Your perfect stay near Har Ki Pauri. Comfort, elegance, and spiritual serenity combined.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/booking">
                <Button size="lg" className="bg-gold-600 hover:bg-gold-700 text-white px-8 py-6 text-lg">
                  Book Your Stay
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/rooms">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-navy-900 px-8 py-6 text-lg">
                  View Rooms
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-white rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Quick Booking Form */}
      <section className="relative -mt-20 z-20 px-4">
        <div className="container mx-auto">
          <Card className="shadow-2xl border-0">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-navy-900 mb-6 text-center">Quick Booking</h3>
              <form onSubmit={handleBookingSubmit} className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={bookingData.name}
                    onChange={(e) => setBookingData({ ...bookingData, name: e.target.value })}
                    placeholder="Your name"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={bookingData.phone}
                    onChange={(e) => setBookingData({ ...bookingData, phone: e.target.value })}
                    placeholder="+91 1234567890"
                    required
                  />
                </div>
                <div>
                  <Label>Check In</Label>
                  <DatePicker
                    date={bookingData.checkIn}
                    onDateChange={(date) => setBookingData({ ...bookingData, checkIn: date })}
                    placeholder="Select date"
                  />
                </div>
                <div>
                  <Label>Check Out</Label>
                  <DatePicker
                    date={bookingData.checkOut}
                    onDateChange={(date) => setBookingData({ ...bookingData, checkOut: date })}
                    placeholder="Select date"
                    minDate={bookingData.checkIn ? new Date(bookingData.checkIn.getTime() + 86400000) : new Date()}
                  />
                </div>
                <div className="flex items-end">
                  <Button type="submit" className="w-full bg-gold-600 hover:bg-gold-700 text-white">
                    Check Availability
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-navy-900 mb-4 font-serif">Why Choose Us</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Experience the perfect blend of comfort, luxury, and authentic hospitality
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Star, title: '5-Star Service', desc: 'Premium hospitality experience' },
              { icon: Award, title: 'Best Location', desc: 'Near Har Ki Pauri temple' },
              { icon: Shield, title: 'Safe & Secure', desc: '24/7 CCTV surveillance' },
              { icon: CheckCircle2, title: 'Best Prices', desc: 'Competitive rates guaranteed' },
            ].map((item, idx) => (
              <Card key={idx} className="border-0 shadow-lg hover:shadow-xl transition-shadow group">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-gold-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-gold-600 transition-colors">
                    <item.icon className="text-gold-600 group-hover:text-white transition-colors" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-navy-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Rooms */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-navy-900 mb-4 font-serif">Our Rooms</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Comfortable and spacious rooms designed for your perfect stay
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {rooms.map((room) => (
              <Card key={room.id} className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-shadow group">
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={room.image}
                    alt={room.name}
                    loading="lazy"
                    width={800}
                    height={400}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 bg-gold-600 text-white px-4 py-2 rounded-full font-bold">
                    ₹{room.price}/night
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-navy-900 mb-2">{room.name}</h3>
                  <p className="text-gray-600 mb-4">{room.description}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {room.amenities.slice(0, 3).map((amenity, idx) => (
                      <span key={idx} className="text-xs bg-gray-100 text-gray-700 px-3 py-1 rounded-full">
                        {amenity}
                      </span>
                    ))}
                  </div>
                  <Link to="/rooms">
                    <Button className="w-full bg-navy-900 hover:bg-navy-800 text-white">
                      View Details
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/rooms">
              <Button size="lg" variant="outline" className="border-gold-600 text-gold-600 hover:bg-gold-600 hover:text-white">
                View All Rooms
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Facilities Highlight */}
      <section className="py-20 bg-navy-900 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 font-serif">Premium Facilities</h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Enjoy world-class amenities during your stay
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {facilities.slice(0, 8).map((facility) => (
              <div key={facility.id} className="text-center p-6 bg-white/5 rounded-lg hover:bg-white/10 transition-colors">
                <div className="w-12 h-12 bg-gold-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl">✓</span>
                </div>
                <h4 className="font-semibold text-white mb-1">{facility.name}</h4>
                <p className="text-sm text-gray-400">{facility.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/facilities">
              <Button size="lg" className="bg-gold-600 hover:bg-gold-700 text-white">
                View All Facilities
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-navy-900 to-navy-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6 font-serif">Ready to Book Your Stay?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Experience the best hospitality in Haridwar. Book now and get exclusive offers!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/booking">
              <Button size="lg" className="bg-gold-600 hover:bg-gold-700 text-white px-8 py-6 text-lg">
                Book Now
              </Button>
            </Link>
            <a href={`tel:${hotelInfo.phone}`}>
              <Button size="lg" variant="outline" className="bg-gold-600 hover:bg-gold-700 text-white px-8 py-6 text-lg">
                <Phone className="mr-2" size={20} />
                Call Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
